import * as React from 'react';
import * as adminStyles from '../AdminStyles.css';
import { inject, observer } from 'mobx-react';
import { NavigatorStore } from '../../stores';
import { IATextField } from '../../../textField/TextField';
import { IAToggle } from '../../../toggle/Toggle';
import { IAIcon } from '../../../icon/Icon';
import { Environment } from '../../../../services/Environment';
import { Utils } from '../Utils';
import { TreeTargeting } from '../../../treeTargeting/TreeTargeting';
import { IADropDown } from '../../../dropDown/DropDown';
import * as Constants from "../../Constants";
import NavigatorService from '../../../../services/NavigatorService';
import { IInstance } from '../../../../interfaces/IInstance';
import { IANumberField } from '../../../numberField/NumberField';
import { ITargetingApplications } from '../../../../interfaces/ITargetingApplications';
import TargetingUtils from '../../../treeTargeting/utils';
import { IAOptionSelector } from '../../../optionSelector/OptionSelector';
import { IAColorPicker } from '../../../colorPicker/ColorPicker';
import { IATermCheckbox } from '../../../termCheckBox/TermCheckbox';
import { LocalizationService } from '../../../../services/LocalizationService';
import { Helper } from '../../../../Helper';
import { IACodeEditor } from '../../../codeEditor/CodeEditor';
import { IAIconPicker } from '../../../iconPicker/IconPicker';
import TopItemSettingsAiConfiguration from './TopItemSettingsAiConfiguration';
import { TreeTargetingForGroups } from '../../../treeTargetingForGroups';
import { IAMediaSelector } from '../../../mediaSelector/MediaSelector';
import { IAButton } from '../../../button/Button';
import { IAMessageBar } from '../../../messageBar/MessageBar';
import { IImageConfettiTemplate } from '../../../../interfaces/IImageConfettiTemplate';

export interface ITopItemSettingsPanelProps {
  navigatorStore?: NavigatorStore;
}

export interface ITopItemSettingsPanelState {
  instances: any;
  applicationSettings: ITargetingApplications;
  isSiteCollectionInValid: boolean;
  categoryValues: any[];
  newConfettiTemplateTitle: string;
}

@inject('navigatorStore')
@observer
export default class TopItemSettingsPanel extends React.Component<ITopItemSettingsPanelProps, ITopItemSettingsPanelState> {
  private localizationService = new LocalizationService();
  private selectedValues: any[];
  // Tracks, per image index, whether its media selector is currently uploading or choosing a source.
  private imageMediaSelectorsBusy: boolean[] = [];

  constructor(props: ITopItemSettingsPanelProps) {
    super(props);
    this.state = {
      instances: undefined,
      applicationSettings: null,
      isSiteCollectionInValid: false,
      categoryValues: undefined,
      newConfettiTemplateTitle: "",
    };
    this.localizationService?.checkLocalizedStrings().then(() => this.forceUpdate());
  }

  private getChildCategories(category: any): any[] {
    if (category) {
      const childCategories = [];
      category.forEach((childData: any) => {
        childCategories.push(
          {
            defaultLabel: childData.name,
            id: childData.guid,
            childNodes: this.getChildCategories(childData.children)
          }
        );
      });
      return childCategories;
    } else {
      return undefined;
    }
  }

  // Marks a template as "selected" purely by comparing image contents, rather than tracking
  // which one was last explicitly picked - so it stays correct across top item switches,
  // reloads, and manually-picked images that happen to match a template exactly.
  private getMatchingConfettiTemplateKey(imageUrls: string[]): string | undefined {
    const currentImageUrls = (imageUrls ?? []).filter((url) => !!url);
    if (currentImageUrls.length === 0) {
      return undefined;
    }
    const allTemplates = [
      ...Constants.IMAGE_CONFETTI_TEMPLATES,
      ...(this.props.navigatorStore.navigator.design.imageConfettiTemplates ?? [])
    ];
    return allTemplates.find((template) =>
      template.imageUrls.length === currentImageUrls.length &&
      template.imageUrls.every((url, index) => url === currentImageUrls[index])
    )?.key;
  }

  // Built-in templates' title/infobox text are only hardcoded Danish fallbacks - prefer the
  // localized string (once translators populate it in the localization DB) when available.
  private getConfettiTemplateText(templateKey: string, field: "title" | "headline" | "message", fallback: string): string {
    const localizationKey = Constants.IMAGE_CONFETTI_TEMPLATE_LOCALIZATION_KEYS[templateKey]?.[field];
    return (localizationKey && (this.localizationService.strings as any)[localizationKey]) || fallback;
  }

  // Lets end users who already deactivated this celebration see it again once the admin
  // changes its settings (see Confetti.tsx's isCelebrationActive/DeactivatedVersion comparison).
  private touchCelebrationVersion(selectedTopItem: any): void {
    if (selectedTopItem?.config) {
      selectedTopItem.config.celebrationVersion = Helper.generateGUID();
    }
  }

  public render(): JSX.Element {
    if (this.props.navigatorStore.isShowingTopItemSettings) {
      let selectedTopItem;
      if (this.props.navigatorStore.topItemIndexToEdit !== undefined) {
        selectedTopItem = this.props.navigatorStore.navigator.megamenu.topItems[this.props.navigatorStore.topItemIndexToEdit];
      } else if (this.props.navigatorStore.smartmenuTopItemIndexToEdit !== undefined) {
        selectedTopItem = this.props.navigatorStore.navigator.smartmenu.elements[this.props.navigatorStore.smartmenuTopItemIndexToEdit];
      }
      this.selectedValues = undefined;
      if (selectedTopItem !== undefined && selectedTopItem.targeting !== undefined && this.props.navigatorStore.applicationSettings) {
        this.selectedValues = Utils.parseSelectedTargetingValues(selectedTopItem.targeting, this.props.navigatorStore.applicationSettings?.applicationSettings);
      }
      if (selectedTopItem !== undefined && (selectedTopItem.type === undefined || selectedTopItem.type === "link")) {
        selectedTopItem.type = "menu";
      } else if (selectedTopItem == undefined) {
        selectedTopItem = {
          type: "menu",
          title: ""
        };
      }
      if (selectedTopItem !== undefined && selectedTopItem.type == "event" || selectedTopItem.type == "contentcreator") {
        let site = undefined;
        switch (selectedTopItem.type) {
          case "event":
            site = "IntraActive-Events"
            break;
          case "contentcreator":
            site = "IntraActive-ContentCreator"
            break;
          default:
            break;
        }
        if (this.props.navigatorStore.instances == undefined) {
          NavigatorService.getInstances(this.props.navigatorStore.token, this.props.navigatorStore.tenant, this.props.navigatorStore.environment, site).then((instances: any[]) => {
            this.props.navigatorStore.instances = instances;
            let availableInstances = [];
            instances.forEach((instance: IInstance) => {
              availableInstances.push({
                key: instance.instanceName,
                value: instance.title
              });
            });
            if (instances.length > 0) {
              if (selectedTopItem.config.instance == undefined) {
                selectedTopItem.config.instance = instances[0];
              }
              this.setState({ instances: availableInstances });
            }
          });
        } else {
          if (this.props.navigatorStore.instances.length > 0 && selectedTopItem.config.instance == undefined) {
            selectedTopItem.config.instance = this.props.navigatorStore.instances[0];
          }
        }
      }

      // Checking if targeting is enabled
      const showTargetOptions = TargetingUtils.checkIfUsesTargeting(this.props.navigatorStore.applicationSettings, this.props.navigatorStore.instance);

      let top = 51;
      switch (Helper.getDisplayEnvironment()) {
        case "ExternalEnvironment":
          top = 51;
          break;
        case "Sharepoint":
          top = 169;
          break;
      }

      const peopleFinderIndex = Constants.TOP_ITEM_TYPES.findIndex(v => v.key === "peoplefinder");
      if (!this.props.navigatorStore.tenantSettings?.peopleFinder?.enabled && peopleFinderIndex != -1) {
        Constants.TOP_ITEM_TYPES.splice(peopleFinderIndex, 1);
      }

      const pages = [];
      if (this.props.navigatorStore.droppableSmartmentTopItemSpotsAreShowing) {
        //props.navigatorStore.navigator.smartmenu.elements
        this.props.navigatorStore.navigator.smartmenu.elements?.forEach((item, index) => {
          pages.push(
            <div
              className={adminStyles.IA_sortingPage}
              key={index}
            >
              {item.config.icon && item.config.icon !== "" && <IAIcon
                title={item.config.icon}
                size={18}
                color={"#333333"}
                style={{
                  float: "left",
                  marginTop: 16,
                  marginLeft: 16
                }}
              />}
              <div
                className={adminStyles.IA_sortingPageText}
                style={{
                  color: item.config.title ? "#333333" : "#999999"
                }}
              >
                {item.config.title}
              </div>
              <IAIcon
                url="https://intraactivestorage.blob.core.windows.net/cdn/icons/Chevron%20Down/SVG/ic_fluent_chevron_down_48_regular.svg"
                size={18}
                color={index < this.props.navigatorStore.navigator.smartmenu.elements.length - 1 ? "#23879b" : "#eeeeee"}
                onClick={() => {
                  if (index < this.props.navigatorStore.navigator.smartmenu.elements.length - 1) {
                    var element = this.props.navigatorStore.navigator.smartmenu.elements[index];
                    this.props.navigatorStore.navigator.smartmenu.elements.splice(index, 1);
                    this.props.navigatorStore.navigator.smartmenu.elements.splice(index + 1, 0, element);
                    this.props.navigatorStore.checkForChanges();
                    // this.props.hasChanged();
                  }
                }}
                style={{
                  float: "right",
                  marginTop: 16,
                  marginRight: 16,
                  cursor: index < this.props.navigatorStore.navigator.smartmenu.elements.length - 1 ? "pointer" : "default"
                }}
              />
              <IAIcon
                url="https://intraactivestorage.blob.core.windows.net/cdn/icons/Chevron%20Up/SVG/ic_fluent_chevron_up_48_regular.svg"
                size={18}
                color={index === 0 || (this.props.navigatorStore.navigator.smartmenu.elements[index]?.type === "IntraActive-NavigatorAdmin" && index === 1) || (this.props.navigatorStore.navigator.smartmenu.elements[index]?.type === "IntraActive-ContentOrganizer" && index === 1) ? "#eeeeee" : "#23879b"}
                onClick={() => {
                  if (index === 0 || (this.props.navigatorStore.navigator.smartmenu.elements[index]?.type === "IntraActive-NavigatorAdmin" && index === 1) || (this.props.navigatorStore.navigator.smartmenu.elements[index]?.type === "IntraActive-ContentOrganizer" && index === 1)) {
                  } else {
                    var element = this.props.navigatorStore.navigator.smartmenu.elements[index];
                    this.props.navigatorStore.navigator.smartmenu.elements.splice(index, 1);
                    this.props.navigatorStore.navigator.smartmenu.elements.splice(index - 1, 0, element);
                    this.props.navigatorStore.checkForChanges();
                    // this.props.hasChanged();
                  }
                }}
                style={{
                  float: "right",
                  marginTop: 16,
                  marginRight: 16,
                  cursor: index > 0 ? "pointer" : "default"
                }}
              />
            </div>
          );
        });
        pages.push(
          <div
            className={adminStyles.IA_sortingPage}
            onClick={() => {
              this.props.navigatorStore.closeReorderSmartMenu();
            }}
            style={{ backgroundColor: "#23879b", marginTop: "40px" }}
          >
            <IAIcon
              title={"Checkmark"}
              size={18}
              color={"#ffffff"}
              style={{
                float: "left",
                marginTop: 16,
                marginLeft: 16
              }}
            />
            <div
              className={adminStyles.IA_sortingPageText}
              style={{
                color: "#ffffff"
              }}
            >
              {this.props.navigatorStore.droppableSmartmentTopItemSpotsAreShowing ? this.localizationService.strings.Navigator_Megamenu_DoneReordering : this.localizationService.strings.Navigator_Megamen_ReorderTopItems}
            </div>
          </div>
        );
      }

      if (this.props.navigatorStore.droppableTopItemSpotsAreShowing) {
        //props.navigatorStore.navigator.smartmenu.elements
        this.props.navigatorStore.navigator.megamenu.topItems?.forEach((item, index) => {
          pages.push(
            <div
              className={adminStyles.IA_sortingPage}
              key={index}
            >
              {item.config.icon && item.config.icon !== "" && <IAIcon
                title={item.config.icon}
                size={18}
                color={"#333333"}
                style={{
                  float: "left",
                  marginTop: 16,
                  marginLeft: 16
                }}
              />}
              <div
                className={adminStyles.IA_sortingPageText}
                style={{
                  color: item.config.title ? "#333333" : "#999999"
                }}
              >
                {item.config.title}
              </div>
              <IAIcon
                url="https://intraactivestorage.blob.core.windows.net/cdn/icons/Chevron%20Down/SVG/ic_fluent_chevron_down_48_regular.svg"
                size={18}
                color={index < this.props.navigatorStore.navigator.megamenu.topItems.length - 1 ? "#23879b" : "#eeeeee"}
                onClick={() => {
                  if (index < this.props.navigatorStore.navigator.megamenu.topItems.length - 1) {
                    var element = this.props.navigatorStore.navigator.megamenu.topItems[index];
                    this.props.navigatorStore.navigator.megamenu.topItems.splice(index, 1);
                    this.props.navigatorStore.navigator.megamenu.topItems.splice(index + 1, 0, element);
                    this.props.navigatorStore.getTopItems();
                    // this.props.hasChanged();
                  }
                }}
                style={{
                  float: "right",
                  marginTop: 16,
                  marginRight: 16,
                  cursor: index < this.props.navigatorStore.navigator.megamenu.topItems.length - 1 ? "pointer" : "default"
                }}
              />
              <IAIcon
                url="https://intraactivestorage.blob.core.windows.net/cdn/icons/Chevron%20Up/SVG/ic_fluent_chevron_up_48_regular.svg"
                size={18}
                color={index === 0 || (this.props.navigatorStore.navigator.megamenu.topItems[index]?.type === "IntraActive-NavigatorAdmin" && index === 1) || (this.props.navigatorStore.navigator.megamenu.topItems[index]?.type === "IntraActive-ContentOrganizer" && index === 1) ? "#eeeeee" : "#23879b"}
                onClick={() => {
                  if (index === 0 || (this.props.navigatorStore.navigator.megamenu.topItems[index]?.type === "IntraActive-NavigatorAdmin" && index === 1) || (this.props.navigatorStore.navigator.megamenu.topItems[index]?.type === "IntraActive-ContentOrganizer" && index === 1)) {
                  } else {
                    var element = this.props.navigatorStore.navigator.megamenu.topItems[index];
                    this.props.navigatorStore.navigator.megamenu.topItems.splice(index, 1);
                    this.props.navigatorStore.navigator.megamenu.topItems.splice(index - 1, 0, element);
                    this.props.navigatorStore.getTopItems();
                    // this.props.hasChanged();
                  }
                }}
                style={{
                  float: "right",
                  marginTop: 16,
                  marginRight: 16,
                  cursor: index > 0 ? "pointer" : "default"
                }}
              />
            </div>
          );
        });
        pages.push(
          <div
            className={adminStyles.IA_sortingPage}
            key={`topItemAddNewButton_${this.props.navigatorStore.navigator.megamenu.topItems.length}`}
            onClick={() => {
              this.props.navigatorStore.closeReorderTopItem();
              this.props.navigatorStore.selectTopMenuItem(undefined);
              this.props.navigatorStore.getTopItems();
            }}
            style={{ backgroundColor: "#23879b", marginTop: "40px" }}
          >
            <IAIcon
              title={"Checkmark"}
              size={18}
              color={"#ffffff"}
              style={{
                float: "left",
                marginTop: 16,
                marginLeft: 16
              }}
            />
            <div
              className={adminStyles.IA_sortingPageText}
              style={{
                color: "#ffffff"
              }}
            >
              {this.props.navigatorStore.droppableTopItemSpotsAreShowing
                ? this.localizationService.strings.Navigator_Megamenu_DoneReordering
                : this.localizationService.strings.Navigator_Megamen_ReorderTopItems}
            </div>
          </div>
        );
      }
      return (
        <div
          className={adminStyles.panel}
          style={{
            right: this.props.navigatorStore.isShowingTopItemSettings ? "0px" : "-400px",
            top: top,
            height: `calc(100% - ${top}px)`,
            overflowY: "hidden"
          }}
        >
          <div className={adminStyles.panelHeader}>
            {this.localizationService.strings.Navigator_Settings_TopItem}
            <IAIcon
              title={"Close"}
              size={20}
              color="#ffffff"
              onClick={() => {
                this.props.navigatorStore.topItemIndexToEdit = undefined;
                this.props.navigatorStore.smartmenuTopItemIndexToEdit = undefined;
                this.props.navigatorStore.closeReorderSmartMenu();
                this.props.navigatorStore.closeReorderTopItem();
                this.props.navigatorStore.toggleTopItemSettings();
              }}
              style={{
                float: "right",
                padding: "10px 10px 0",
                cursor: "pointer",
                lineHeight: 0
              }}
            />
          </div>

          <div
            style={{
              width: "100%",
              height: "100%",
              overflowY: "auto",
              padding: "0 20px 20px",
              boxSizing: "border-box"
            }}
          >
            {this.props.navigatorStore.isShowingTopItemSettings && !this.props.navigatorStore.droppableTopItemSpotsAreShowing &&
              !this.props.navigatorStore.droppableSmartmentTopItemSpotsAreShowing && selectedTopItem !== undefined &&
              <>
                {this.props.navigatorStore.smartmenuTopItemIndexToEdit !== undefined &&
                  <IADropDown
                    label={this.localizationService.strings.Navigator_Content_TopItemType}
                    selectedOption={selectedTopItem?.type ?? undefined}
                    options={Constants.SMARTMENU_TOP_ITEM_TYPES.concat(this.props.navigatorStore.getCodeSnippetComponentIfEnabled()).concat(this.props.navigatorStore.getAIComponentIfEnabled()).concat(this.props.navigatorStore.getTargetedPreviewComponentIfEnabled())}
                    borderRadius={5}
                    onChange={(type: string) => {
                      this.props.navigatorStore.instances = undefined;
                      if (this.props.navigatorStore.smartmenuTopItemIndexToEdit !== undefined && type) {
                        this.props.navigatorStore.setSmartmenuTopItemType(type.toLowerCase());
                      }
                      this.forceUpdate();
                    }}
                  />
                }
                {this.props.navigatorStore.smartmenuTopItemIndexToEdit !== undefined &&
                  <div className={adminStyles.panelDivider} />
                }
                <IATextField
                  label={this.localizationService.strings.Navigator_Content_Titel}
                  text={selectedTopItem.config.title}
                  borderRadius={5}
                  onChange={(title: string) => {
                    if (this.props.navigatorStore.smartmenuTopItemIndexToEdit !== undefined) {
                      this.props.navigatorStore.setSmartmenuTopItemTitle(title);
                    } else if (this.props.navigatorStore.topItemIndexToEdit !== undefined) {
                      this.props.navigatorStore.setTopItemTitle(title);
                    }
                    this.forceUpdate();
                  }}
                />
                <IAIconPicker
                  label={this.localizationService.strings.Navigator_Content_Icon}
                  selectedIcon={selectedTopItem.config.icon}
                  color={"#333333"}
                  select={(icon: string) => {
                    if (this.props.navigatorStore.smartmenuTopItemIndexToEdit !== undefined) {
                      this.props.navigatorStore.setSmartmenuTopItemIcon(icon);
                    } else if (this.props.navigatorStore.topItemIndexToEdit !== undefined) {
                      this.props.navigatorStore.setTopItemIcon(icon);
                    }
                    this.forceUpdate();
                  }}
                  shouldBeAbleToClear={this.props.navigatorStore.smartmenuTopItemIndexToEdit === undefined}
                  clear={() => {
                    if (this.props.navigatorStore.smartmenuTopItemIndexToEdit !== undefined) {
                      this.props.navigatorStore.setSmartmenuTopItemIcon(undefined);
                    } else if (this.props.navigatorStore.topItemIndexToEdit !== undefined) {
                      this.props.navigatorStore.setTopItemIcon(undefined);
                    }
                    this.forceUpdate();
                  }}
                />
                <div className={adminStyles.panelDivider} />
                {selectedTopItem.type === "targetedpreview" &&
                  <div style={{ marginTop: 20, float: "left", width: "100%" }}>
                    <TreeTargetingForGroups
                      environment={this.props.navigatorStore.environment}
                      tenant={this.props.navigatorStore.tenant.replace(".sharepoint.com", "")}
                      application={"navigator"}
                      label={this.localizationService.strings.Navigator_TargetedPreview_GroupsToDisplay}
                      instance={this.props.navigatorStore.instance}
                      applicationData={this.props.navigatorStore.applicationSettings}
                      selectedGroupIds={selectedTopItem?.config?.groupsToDisplay}
                      highlightColor={this.props.navigatorStore.adminColor}
                      showTarget
                      hideOptions
                      onShowTargetChanged={(showTarget: boolean) => {
                        if (!selectedTopItem.targeting) {
                          selectedTopItem.targeting = {
                            enabled: showTarget,
                            operator: "AND"
                          };
                        } else {
                          selectedTopItem.targeting.enabled = showTarget;
                        }
                        this.props.navigatorStore.checkForChanges();
                      }}
                      onGroupIdsUpdated={(ids: number[]) => {
                        selectedTopItem.config.groupsToDisplay = ids;
                        this.forceUpdate();
                        this.props.navigatorStore.checkForChanges();
                      }}
                    />
                  </div>
                }
                {selectedTopItem.type === "menu" &&
                  <IATextField
                    label={this.localizationService.strings.Navigator_Content_UrlTopItem}
                    text={selectedTopItem.config.url}
                    borderRadius={5}
                    onChange={(url: string) => {
                      if (this.props.navigatorStore.smartmenuTopItemIndexToEdit !== undefined) {
                        this.props.navigatorStore.setSmartmenuTopItemUrl(url);
                      } else if (this.props.navigatorStore.topItemIndexToEdit !== undefined) {
                        this.props.navigatorStore.setTopItemUrl(url);
                      }
                      this.forceUpdate();
                    }}
                  />
                }
                {selectedTopItem.type === "menu" &&
                  <IAToggle
                    label={this.localizationService.strings.Navigator_Content_LinkOpen}
                    checked={selectedTopItem.config.openInNewWindow}
                    color={"#333333"}
                    onChange={() => {
                      if (this.props.navigatorStore.smartmenuTopItemIndexToEdit !== undefined) {
                        this.props.navigatorStore.setSmartmenuTopItemOpenInNewWindow();
                      } else if (this.props.navigatorStore.topItemIndexToEdit !== undefined) {
                        this.props.navigatorStore.setTopItemOpenInNewWindow();
                      }
                      this.forceUpdate();
                    }}
                    style={{
                      marginTop: 20
                    }}
                  />
                }
                {selectedTopItem.type === "code" &&
                  <IACodeEditor
                    label={this.localizationService.strings.Navigator_Content_CodeEditor}
                    code={selectedTopItem.config.code}
                    highlightColor={this.props.navigatorStore.adminColor}
                    required
                    onChange={(code: string) => {
                      if (this.props.navigatorStore.smartmenuTopItemIndexToEdit !== undefined) {
                        this.props.navigatorStore.setSmartmenuTopItemCode(code);
                      } else if (this.props.navigatorStore.topItemIndexToEdit !== undefined) {
                        this.props.navigatorStore.setTopItemCode(code);
                      }
                      this.forceUpdate();
                    }}
                  />
                }
                {selectedTopItem.type == "phonebook" &&
                  <>
                    <IATextField
                      label={this.localizationService.strings.Navigator_Content_SiteCollectionTopItem}
                      text={selectedTopItem.config.siteCollection}
                      borderRadius={5}
                      onChange={(siteCollection: string) => {
                        if (this.props.navigatorStore.smartmenuTopItemIndexToEdit !== undefined) {
                          this.props.navigatorStore.setSmartmenuTopItemSiteCollection(siteCollection);
                        } else if (this.props.navigatorStore.topItemIndexToEdit !== undefined) {
                          this.props.navigatorStore.setTopItemSiteCollection(siteCollection);
                        }
                        this.forceUpdate();
                      }}
                    />
                    <IATextField
                      label={this.localizationService.strings.Navigator_Content_ResultSource}
                      text={selectedTopItem.config.resultSource}
                      borderRadius={5}
                      onChange={(resultSource: string) => {
                        if (this.props.navigatorStore.smartmenuTopItemIndexToEdit !== undefined) {
                          this.props.navigatorStore.setSmartmenuTopItemResultSource(resultSource);
                        } else if (this.props.navigatorStore.topItemIndexToEdit !== undefined) {
                          this.props.navigatorStore.setTopItemResultSource(resultSource);
                        }
                        this.forceUpdate();
                      }}
                    />
                    {(/[^\dA-z\-\/]+/.test(selectedTopItem.config.siteCollection) || (selectedTopItem.config.siteCollection === "")) && // check if contain anything but "white space", letter, digit, -, /
                      <div style={{ color: 'red', fontSize: 14, marginTop: 10, float: 'left' }}>
                        {this.localizationService.strings.Navigator_Content_SiteCollectionInputWarning}
                      </div>
                    }
                  </>
                }
                {selectedTopItem && selectedTopItem.type === "celebration" &&
                  <IADropDown
                    label={this.localizationService.strings.Navigator_Content_CelebrationType}
                    selectedOption={selectedTopItem !== undefined && selectedTopItem.config !== undefined ? selectedTopItem.config.subType : undefined}
                    borderRadius={5}
                    options={[
                      {
                        key: 'snow',
                        value: 'Sne',
                      },
                      {
                        key: 'confetti',
                        value: "Klassisk konfetti",
                      },
                      {
                        key: 'imageconfetti',
                        value: this.localizationService.strings.Navigator_ImageConfetti,
                      }
                    ]}
                    style={{
                      marginTop: 15,
                      float: "left"
                    }}
                    onChange={(subType: string) => {
                      if (this.props.navigatorStore.celebrationPreview) {
                        this.props.navigatorStore.stopCelebrationPreview();
                      }
                      if (this.props.navigatorStore.smartmenuTopItemIndexToEdit !== undefined && subType) {
                        this.props.navigatorStore.setSmartmenuSubType(subType.toLowerCase());
                        this.touchCelebrationVersion(selectedTopItem);
                      }
                      this.forceUpdate();
                    }}
                  />
                }
                {selectedTopItem && selectedTopItem.config &&
                  (selectedTopItem.config.subType === "confetti" || selectedTopItem.config.subType === "imageconfetti" || selectedTopItem.config.subType === "snow") &&
                  <>
                    <IANumberField
                      label={this.localizationService.strings.Navigator_Content_CelebrationDuration ?? "Duration (seconds)"}
                      number={selectedTopItem.config.durationInSeconds}
                      min={0}
                      borderRadius={5}
                      onChange={(durationInSeconds: number) => {
                        selectedTopItem.config.durationInSeconds = durationInSeconds;
                        this.touchCelebrationVersion(selectedTopItem);
                        this.props.navigatorStore.checkForChanges();
                        this.forceUpdate();
                      }}
                      styles={{
                        clear: "both",
                        marginTop: 15,
                        width: "100%"
                      }}
                    />
                    <IAMessageBar
                      type="information"
                      content={this.localizationService.strings.Navigator_Admin_ConfettiDurationInfo}
                      style={{
                        marginTop: 12,
                        float: "left",
                        borderRadius: 5,
                        width: "calc(100% - 15px)"
                      }}
                      onDismiss={() => { }}
                    />
                  </>
                }
                {selectedTopItem && selectedTopItem.config && selectedTopItem.config.subType === "imageconfetti" &&
                  <>
                    <label style={{ marginTop: 20, float: "left", width: "100%", fontSize: 14 }}>
                      {this.localizationService.strings.Navigator_Admin_ImageConfettiTemplates ?? "Templates"}
                    </label>
                    <IAOptionSelector
                      selected={this.getMatchingConfettiTemplateKey(selectedTopItem.config.imageUrls)}
                      highlightColor={this.props.navigatorStore.adminColor}
                      borderRadius={5}
                      style={{ marginTop: 10, float: "left", width: "100%" }}
                      environment={this.props.navigatorStore.environment}
                      tenant={this.props.navigatorStore.tenant}
                      token={this.props.navigatorStore.token}
                      options={[
                        ...Constants.IMAGE_CONFETTI_TEMPLATES,
                        ...(this.props.navigatorStore.navigator.design.imageConfettiTemplates ?? [])
                      ].map((template) => ({
                        key: template.key,
                        text: this.getConfettiTemplateText(template.key, "title", template.title),
                        image: template.imageUrls[0],
                        width: 100,
                        onDelete: template.key.startsWith("custom-") ? () => {
                          this.props.navigatorStore.deleteImageConfettiTemplate(template.key);
                          this.forceUpdate();
                        } : undefined
                      }))}
                      onChange={(templateKey: string) => {
                        const template: IImageConfettiTemplate = [
                          ...Constants.IMAGE_CONFETTI_TEMPLATES,
                          ...(this.props.navigatorStore.navigator.design.imageConfettiTemplates ?? [])
                        ].find((t) => t.key === templateKey);
                        if (template) {
                          selectedTopItem.config.imageUrls = [...template.imageUrls];
                          selectedTopItem.config.imageUrl = undefined;
                          selectedTopItem.config.showConfettiInfobox = template.showConfettiInfobox;
                          selectedTopItem.config.confettiInfoboxHeadline = template.confettiInfoboxHeadline && this.getConfettiTemplateText(template.key, "headline", template.confettiInfoboxHeadline);
                          selectedTopItem.config.confettiInfoboxMessage = template.confettiInfoboxMessage && this.getConfettiTemplateText(template.key, "message", template.confettiInfoboxMessage);
                          selectedTopItem.config.confettiInfoboxBackgroundColor = template.confettiInfoboxBackgroundColor;
                          selectedTopItem.config.confettiInfoboxTextColor = template.confettiInfoboxTextColor;
                          this.touchCelebrationVersion(selectedTopItem);
                          this.imageMediaSelectorsBusy = [];
                          this.props.navigatorStore.checkForChanges();
                          this.forceUpdate();
                        }
                        if (this.props.navigatorStore.celebrationPreview) {
                          this.props.navigatorStore.stopCelebrationPreview();
                        }
                      }}
                    />
                    {this.localizationService.strings.Navigator_Content_ElementImage &&
                      <label style={{ marginTop: 20, float: "left", width: "100%", fontSize: 14 }}>
                        {this.localizationService.strings.Navigator_Admin_ImageConfettiImages ?? "Images"}
                      </label>
                    }
                    <div style={{ clear: "both", marginTop: 20, marginBottom: 10, float: "left" }}>
                      <div style={{ clear: "both", display: "flex", flexWrap: "wrap", gap: 10 }}>
                        {(selectedTopItem.config.imageUrls && selectedTopItem.config.imageUrls.length > 0 ? selectedTopItem.config.imageUrls : [selectedTopItem.config.imageUrl ?? ""]).map((imageUrl: string, imageIndex: number) => (
                          <IAMediaSelector
                            key={"imageConfettiImage_" + imageIndex}
                            showPreview={false}
                            fileType="image"
                            displayMode="grid"
                            highlightColor={this.props.navigatorStore.navigator?.design?.menuBar?.highlightColor}
                            token={this.props.navigatorStore.token}
                            tenant={this.props.navigatorStore.tenant.substring(0, this.props.navigatorStore.tenant.indexOf('.'))}
                            selectedImage={imageUrl}
                            environment={this.props.navigatorStore.environment}
                            uploadUrl={`${Environment.getEnvironmentForMainAPI(this.props.navigatorStore.environment)}/api/${this.props.navigatorStore.tenant}/image?site=IntraActive-NavigatorAdmin&relativeUrl=${encodeURIComponent("lists/IANavigatorImages")}`}
                            // Confetti images are only ever rendered at ~64px, so
                            // downscale oversized uploads client-side to avoid
                            // needlessly large uploads/downloads.
                            maxImageDimension={512}
                            onChange={(newImageUrl: string) => {
                              const imageUrls = selectedTopItem.config.imageUrls && selectedTopItem.config.imageUrls.length > 0 ? [...selectedTopItem.config.imageUrls] : [selectedTopItem.config.imageUrl ?? ""];
                              // Grid mode's corner "x" clears the tile - when it's not the only
                              // one left, drop it entirely instead of leaving an empty tile behind.
                              if (!newImageUrl && imageUrls.length > 1) {
                                imageUrls.splice(imageIndex, 1);
                                this.imageMediaSelectorsBusy.splice(imageIndex, 1);
                              } else {
                                imageUrls[imageIndex] = newImageUrl;
                              }
                              selectedTopItem.config.imageUrls = imageUrls;
                              selectedTopItem.config.imageUrl = undefined;
                              this.touchCelebrationVersion(selectedTopItem);
                              if (this.props.navigatorStore.celebrationPreview) {
                                this.props.navigatorStore.stopCelebrationPreview();
                              }
                              this.props.navigatorStore.checkForChanges();
                              this.forceUpdate();
                            }}
                            onBusyChange={(isBusy) => {
                              this.imageMediaSelectorsBusy[imageIndex] = isBusy;
                              this.forceUpdate();
                            }}
                          />
                        ))}
                      </div>
                    </div>
                    <IAButton
                      label={this.localizationService.strings.Editor_AddImage}
                      buttonColor={this.props.navigatorStore.adminColor}
                      textColor="#ffffff"
                      showSpinner={false}
                      disbaled={this.imageMediaSelectorsBusy.some((isBusy) => isBusy)}
                      fontSize={14}
                      onClick={() => {
                        const imageUrls = selectedTopItem.config.imageUrls && selectedTopItem.config.imageUrls.length > 0 ? [...selectedTopItem.config.imageUrls] : [selectedTopItem.config.imageUrl ?? ""];
                        imageUrls.push("");
                        selectedTopItem.config.imageUrls = imageUrls;
                        selectedTopItem.config.imageUrl = undefined;
                        this.touchCelebrationVersion(selectedTopItem);
                        this.props.navigatorStore.checkForChanges();
                        this.forceUpdate();
                      }}
                      style={{
                        clear: "both",
                        marginTop: 12,
                        float: "left",
                        borderRadius: 5
                      }}
                    />
                    <IAMessageBar
                      type="information"
                      content={this.localizationService.strings.Navigator_Admin_ImageConfettiInfo}
                      style={{
                        marginTop: 12,
                        float: "left",
                        borderRadius: 5,
                        width: "calc(100% - 15px)"
                      }}
                      onDismiss={() => { }}
                    />
                  </>
                }
                {selectedTopItem && selectedTopItem.config &&
                  (selectedTopItem.config.subType === "confetti" || selectedTopItem.config.subType === "imageconfetti" || selectedTopItem.config.subType === "snow") &&
                  <IAButton
                    label={this.props.navigatorStore.celebrationPreview
                      ? (this.localizationService.strings.Navigator_Content_EndPreviewCelebration ?? "End preview")
                      : (this.localizationService.strings.Navigator_Content_PreviewCelebration ?? "Preview")}
                    buttonColor={this.props.navigatorStore.adminColor}
                    textColor="#ffffff"
                    showSpinner={false}
                    fontSize={14}
                    onClick={() => {
                      if (this.props.navigatorStore.celebrationPreview) {
                        this.props.navigatorStore.stopCelebrationPreview();
                        return;
                      }
                      const imageUrls = selectedTopItem.config.imageUrls && selectedTopItem.config.imageUrls.length > 0
                        ? selectedTopItem.config.imageUrls
                        : selectedTopItem.config.imageUrl ? [selectedTopItem.config.imageUrl] : [];
                      this.props.navigatorStore.previewCelebration(
                        selectedTopItem.config.subType,
                        imageUrls,
                        selectedTopItem.config.durationInSeconds,
                        selectedTopItem.config.showConfettiInfobox,
                        selectedTopItem.config.confettiInfoboxHeadline,
                        selectedTopItem.config.confettiInfoboxMessage,
                        selectedTopItem.config.confettiInfoboxBackgroundColor,
                        selectedTopItem.config.confettiInfoboxTextColor
                      );
                    }}
                    style={{
                      clear: "both",
                      marginTop: 15,
                      float: "left",
                      borderRadius: 5
                    }}
                  />
                }
                {selectedTopItem && selectedTopItem.config && (selectedTopItem.config.subType === "imageconfetti" || selectedTopItem.config.subType === "snow" || selectedTopItem.config.subType === "confetti") &&
                  <>
                    <div className={adminStyles.panelDivider} />
                    <IAToggle
                      label={this.localizationService.strings.Navigator_Content_ShowConfettiInfoBox ?? "Vis infoboks"}
                      checked={selectedTopItem.config.showConfettiInfobox !== undefined ? selectedTopItem.config.showConfettiInfobox : false}
                      color={this.props.navigatorStore.adminColor}
                      onChange={() => {
                        selectedTopItem.config.showConfettiInfobox = selectedTopItem.config.showConfettiInfobox != undefined ? !selectedTopItem.config.showConfettiInfobox : true;
                        this.touchCelebrationVersion(selectedTopItem);
                        this.props.navigatorStore.checkForChanges();
                        this.forceUpdate();
                      }}
                      style={{  
                        marginTop: 20
                      }}
                    />
                    <IATextField
                      label={this.localizationService.strings.Navigator_Content_ConfettiInfoBoxHeadline ?? "Infobox overskrift"}
                      text={selectedTopItem.config.confettiInfoboxHeadline}
                      borderRadius={5}
                      disabled={!selectedTopItem?.config.showConfettiInfobox}
                      onChange={(confettiInfoboxHeadline: string) => {
                        selectedTopItem.config.confettiInfoboxHeadline = confettiInfoboxHeadline;
                        this.touchCelebrationVersion(selectedTopItem);
                        this.props.navigatorStore.checkForChanges();
                        this.forceUpdate();
                      }}
                      style={{
                        clear: "both",
                        marginTop: 20,
                        width: "100%"
                      }}
                    />
                    {/* uses a plain textarea instead of IATextField to allow multiline input */}
                    <div
                      style={{
                        clear: "both",
                        marginTop: 20,
                        float: "left",
                        width: "100%"
                      }}
                    >
                      <label style={{ float: "left", width: "100%", fontSize: 14 }}>
                        {this.localizationService.strings.Navigator_Content_ConfettiInfoBoxMessage ?? "Infobox besked"}
                      </label>
                      <textarea
                        value={selectedTopItem.config.confettiInfoboxMessage || ""}
                        disabled={!selectedTopItem.config.showConfettiInfobox}
                        style={{
                          width: "100%",
                          marginTop: 7,
                          padding: 10,
                          borderRadius: 5,
                          height: 80,
                          boxSizing: "border-box",
                          resize: "vertical",
                          fontFamily: "inherit",
                          border: "1px solid #dddddd"
                        }}
                        onChange={(event) => {
                          selectedTopItem.config.confettiInfoboxMessage = event.target.value;
                          this.touchCelebrationVersion(selectedTopItem);
                          this.props.navigatorStore.checkForChanges();
                          this.forceUpdate();
                        }}
                      />
                    </div>
                    <IAColorPicker
                      label={this.localizationService.strings.Navigator_Content_ConfettiInfoBoxBackgroundColor ?? "Infobox baggrundsfarve"}
                      color={selectedTopItem.config.confettiInfoboxBackgroundColor}
                      disabled={!selectedTopItem.config.showConfettiInfobox}
                      onChange={(confettiInfoboxBackgroundColor: string) => {
                        selectedTopItem.config.confettiInfoboxBackgroundColor = confettiInfoboxBackgroundColor;
                        this.touchCelebrationVersion(selectedTopItem);
                        this.props.navigatorStore.checkForChanges();
                        this.forceUpdate();
                      }}
                      styles={{
                        clear: "both",
                        marginTop: 20,
                        width: "100%"
                      }}
                      showClearButton={true}
                    />
                    <IAColorPicker
                      label={this.localizationService.strings.Navigator_Content_ConfettiInfoBoxTextColor ?? "Infobox tekstfarve"}
                      color={selectedTopItem.config.confettiInfoboxTextColor}
                      disabled={!selectedTopItem.config.showConfettiInfobox}
                      onChange={(confettiInfoboxTextColor: string) => {
                        selectedTopItem.config.confettiInfoboxTextColor = confettiInfoboxTextColor;
                        this.touchCelebrationVersion(selectedTopItem);
                        this.props.navigatorStore.checkForChanges();
                        this.forceUpdate();
                      }}
                      styles={{
                        clear: "both",
                        marginTop: 20,
                        width: "100%"
                      }}
                      showClearButton={true}
                    />
                  </>
                }
                {selectedTopItem && selectedTopItem.config && selectedTopItem.config.subType === "imageconfetti" &&
                  <>
                    <div className={adminStyles.panelDivider} />
                    <IATextField
                      label={this.localizationService.strings.Navigator_Content_NewTemplateName ?? "New template name"}
                      text={this.state.newConfettiTemplateTitle}
                      borderRadius={5}
                      onChange={(newConfettiTemplateTitle: string) => this.setState({ newConfettiTemplateTitle })}
                      style={{
                        clear: "both",
                        marginTop: 20,
                        width: "100%"
                      }}
                    />
                    <IAButton
                      label={this.localizationService.strings.Navigator_Content_SaveAsTemplate ?? "Save as template"}
                      buttonColor={this.props.navigatorStore.adminColor}
                      textColor="#ffffff"
                      showSpinner={false}
                      disbaled={!this.state.newConfettiTemplateTitle || !(selectedTopItem.config.imageUrls?.some((url: string) => url))}
                      fontSize={14}
                      onClick={() => {
                        const imageUrls = (selectedTopItem.config.imageUrls ?? []).filter((url: string) => url);
                        if (!this.state.newConfettiTemplateTitle || imageUrls.length === 0) {
                          return;
                        }
                        this.props.navigatorStore.addImageConfettiTemplate(
                          this.state.newConfettiTemplateTitle,
                          imageUrls,
                          selectedTopItem.config.showConfettiInfobox,
                          selectedTopItem.config.confettiInfoboxHeadline,
                          selectedTopItem.config.confettiInfoboxMessage,
                          selectedTopItem.config.confettiInfoboxBackgroundColor,
                          selectedTopItem.config.confettiInfoboxTextColor
                        );
                        this.setState({ newConfettiTemplateTitle: "" });
                      }}
                      style={{
                        clear: "both",
                        marginTop: 10,
                        float: "left",
                        borderRadius: 5
                      }}
                    />
                    <IAMessageBar
                      type="information"
                      content={this.localizationService.strings.Navigator_Admin_ImageConfettiTemplateInfo}
                      style={{
                        marginTop: 12,
                        float: "left",
                        borderRadius: 5,
                        width: "calc(100% - 15px)"
                      }}
                      onDismiss={() => { }}
                    />
                  </>
                }
                {(selectedTopItem.type == "event" || selectedTopItem.type == "contentcreator") && this.state.instances &&
                  <>
                    <IADropDown
                      label={this.localizationService.strings.Navigator_Admin_SelectInstance}
                      selectedOption={selectedTopItem.config.instance != undefined ? selectedTopItem.config.instance.instanceName : undefined}
                      options={this.state.instances}
                      borderRadius={5}
                      onChange={(instanceName: string) => {
                        this.props.navigatorStore.instances.forEach((instance: IInstance) => {
                          if (instance.instanceName === instanceName) {
                            selectedTopItem.config.instance = instance;
                            this.props.navigatorStore.checkForChanges();
                            this.forceUpdate();
                          }
                        });

                        if (selectedTopItem.config.shouldFilterByCategory) {
                          selectedTopItem.config.shouldFilterByCategory = false;
                        }
                      }}
                      style={{
                        float: "left",
                        opacity: this.state.instances ? 1 : 0.5
                      }}
                    />
                  </>
                }
                {selectedTopItem.type == "event" && this.state.instances &&
                  <>
                    <IADropDown
                      label={this.localizationService.strings.Navigator_Admin_SelectPeriod}
                      selectedOption={selectedTopItem.config.showPeriod}
                      options={Constants.SHOW_PERIOD}
                      borderRadius={5}
                      onChange={(showPeriod: string) => {
                        selectedTopItem.config.showPeriod = showPeriod;
                        this.props.navigatorStore.checkForChanges();
                        this.forceUpdate();
                      }}
                      style={{
                        float: "left",
                        opacity: this.state.instances ? 1 : 0.5
                      }}
                    />
                    <div className={adminStyles.panelDivider} />
                    {selectedTopItem.config.showPeriod && selectedTopItem.config.showPeriod !== "fullView" &&
                      <IAOptionSelector
                        label={"Visningstype"}
                        options={[
                          {
                            icon: "Card view",
                            key: "Card view",
                            text: "Card view"
                          },
                          {
                            icon: "Thumbnail list",
                            key: "List view",
                            text: "List view",
                          }
                        ]}
                        selected={selectedTopItem.config.displayType != undefined ? selectedTopItem.config.displayType : "Card view"}
                        highlightColor={this.props.navigatorStore.adminColor}
                        borderRadius={5}
                        onChange={(option: string) => {
                          selectedTopItem.config.displayType = option;
                          this.props.navigatorStore.checkForChanges();
                          this.forceUpdate();
                        }}
                      />
                    }
                    {selectedTopItem.config.showPeriod && selectedTopItem.config.showPeriod !== "fullView" &&
                      <IANumberField
                        label={this.localizationService.strings.Navigator_Admin_AmountOfCardsPerPage}
                        number={selectedTopItem.config.numberOfEvents}
                        onChange={(numberOfEvents: number) => {
                          selectedTopItem.config.numberOfEvents = numberOfEvents;
                          this.props.navigatorStore.checkForChanges();
                          this.forceUpdate();
                        }}
                        borderRadius={5}
                        styles={{
                          width: "100%"
                        }}
                      />
                    }
                    {selectedTopItem.config.showPeriod && selectedTopItem.config.showPeriod !== "fullView" &&
                      <IAColorPicker
                        label={this.localizationService.strings.Navigator_Design_BackgroundColor}
                        color={selectedTopItem.config.backgroundColor}
                        onChange={(backgroundColor: string) => {
                          selectedTopItem.config.backgroundColor = backgroundColor;
                          this.props.navigatorStore.checkForChanges();
                          this.forceUpdate();
                        }}
                        styles={{
                          width: "100%"
                        }}
                        showClearButton={true}
                      />
                    }
                    {selectedTopItem.config.showPeriod && selectedTopItem.config.showPeriod !== "fullView" &&
                      <IAColorPicker
                        label={this.localizationService.strings.Navigator_Design_TextColor}
                        color={selectedTopItem.config.textColor}
                        onChange={(textColor: string) => {
                          selectedTopItem.config.textColor = textColor;
                          this.props.navigatorStore.checkForChanges();
                          this.forceUpdate();
                        }}
                        styles={{
                          width: "100%"
                        }}
                        showClearButton={true}
                      />
                    }
                    {selectedTopItem.config.displayType === "List view" && selectedTopItem.config.showPeriod !== "fullView" &&
                      <IAColorPicker
                        label={this.localizationService.strings.Navigator_Design_DateBadgeBackgroundColor}
                        color={selectedTopItem.config.dateBadgeBackgroundColor}
                        onChange={(dateBadgeBackgroundColor: string) => {
                          selectedTopItem.config.dateBadgeBackgroundColor = dateBadgeBackgroundColor;
                          this.props.navigatorStore.checkForChanges();
                          this.forceUpdate();
                        }}
                        styles={{
                          width: "100%"
                        }}
                        showClearButton={true}
                      />
                    }
                    {selectedTopItem.config.displayType === "List view" && selectedTopItem.config.showPeriod !== "fullView" &&
                      <IAColorPicker
                        label={this.localizationService.strings.Navigator_Design_DateBadgeTextColor}
                        color={selectedTopItem.config.dateBadgeTextColor}
                        onChange={(dateBadgeTextColor: string) => {
                          selectedTopItem.config.dateBadgeTextColor = dateBadgeTextColor;
                          this.props.navigatorStore.checkForChanges();
                          this.forceUpdate();
                        }}
                        styles={{
                          width: "100%"
                        }}
                        showClearButton={true}
                      />
                    }
                    {selectedTopItem.config.showPeriod && selectedTopItem.config.showPeriod !== "fullView" &&
                      <IAColorPicker
                        label={this.localizationService.strings.Navigator_Design_BorderColor}
                        color={selectedTopItem.config.borderColor}
                        onChange={(borderColor: string) => {
                          selectedTopItem.config.borderColor = borderColor;
                          this.props.navigatorStore.checkForChanges();
                          this.forceUpdate();
                        }}
                        styles={{
                          width: "100%"
                        }}
                        showClearButton={true}
                      />
                    }
                    {selectedTopItem.config.showPeriod && selectedTopItem.config.showPeriod !== "fullView" &&
                      <IAToggle
                        label={this.localizationService.strings.Navigator_Content_ShowDropShadow}
                        checked={selectedTopItem.config.showDropShadow !== undefined ? selectedTopItem.config.showDropShadow : false}
                        color={this.props.navigatorStore.adminColor}
                        onChange={() => {
                          selectedTopItem.config.showDropShadow = selectedTopItem.config.showDropShadow != undefined ? !selectedTopItem.config.showDropShadow : true;
                          this.props.navigatorStore.checkForChanges();
                          this.forceUpdate();
                        }}
                        style={{
                          marginTop: 20
                        }}
                      />
                    }
                    <IAToggle
                      label={this.localizationService.strings.Navigator_Content_AddMessage}
                      checked={selectedTopItem.config.addMessage !== undefined ? selectedTopItem.config.addMessage : true}
                      color={this.props.navigatorStore.adminColor}
                      onChange={() => {
                        selectedTopItem.config.addMessage = selectedTopItem.config.addMessage != undefined ? !selectedTopItem.config.addMessage : false;
                        this.props.navigatorStore.checkForChanges();
                        this.forceUpdate();
                      }}
                      style={{
                        marginTop: 20
                      }}
                    />
                    {selectedTopItem != undefined && !selectedTopItem.config.shouldFilterByCategory &&
                      <IAToggle
                        label={this.localizationService.strings.Navigator_Content_ShowFilter}
                        checked={selectedTopItem.config.showFilter !== undefined ? selectedTopItem.config.showFilter : false}
                        color={this.props.navigatorStore.adminColor}
                        onChange={() => {
                          selectedTopItem.config.showFilter = selectedTopItem.config.showFilter != undefined ? !selectedTopItem.config.showFilter : true;
                          this.props.navigatorStore.checkForChanges();
                          this.forceUpdate();
                        }}
                        style={{
                          marginTop: 20
                        }}
                      />
                    }
                    {selectedTopItem.config.showPeriod && selectedTopItem.config.showPeriod !== "fullView" &&
                      <IAToggle
                        label={this.localizationService.strings.Navigator_Content_ShowTimespand}
                        checked={selectedTopItem.config.showTimespand !== undefined ? selectedTopItem.config.showTimespand : true}
                        color={this.props.navigatorStore.adminColor}
                        onChange={() => {
                          selectedTopItem.config.showTimespand = selectedTopItem.config.showTimespand != undefined ? !selectedTopItem.config.showTimespand : false;
                          this.props.navigatorStore.checkForChanges();
                          this.forceUpdate();
                        }}
                        style={{
                          marginTop: 20
                        }}
                      />
                    }
                    {selectedTopItem.config.showPeriod && selectedTopItem.config.showPeriod !== "fullView" &&
                      <IAToggle
                        label={this.localizationService.strings.Navigator_Content_ShowCategories}
                        checked={selectedTopItem.config.showCategories !== undefined ? selectedTopItem.config.showCategories : true}
                        color={this.props.navigatorStore.adminColor}
                        onChange={() => {
                          selectedTopItem.config.showCategories = selectedTopItem.config.showCategories != undefined ? !selectedTopItem.config.showCategories : false;
                          this.props.navigatorStore.checkForChanges();
                          this.forceUpdate();
                        }}
                        style={{
                          marginTop: 20
                        }}
                      />
                    }
                    <IAToggle
                      label={this.localizationService.strings.Navigator_Content_ShowPaging}
                      checked={selectedTopItem.config.showPaging !== undefined ? selectedTopItem.config.showPaging : true}
                      color={this.props.navigatorStore.adminColor}
                      onChange={() => {
                        selectedTopItem.config.showPaging = selectedTopItem.config.showPaging != undefined ? !selectedTopItem.config.showPaging : false;
                        this.props.navigatorStore.checkForChanges();
                        this.forceUpdate();
                      }}
                      style={{
                        marginTop: 20
                      }}
                    />
                    <div className={adminStyles.panelDivider} />
                    <IAToggle
                      label={this.localizationService.strings.Navigator_Content_EnableLikes}
                      checked={selectedTopItem.config.enableLikes !== undefined ? selectedTopItem.config.enableLikes : true}
                      color={this.props.navigatorStore.adminColor}
                      onChange={() => {
                        if (this.props.navigatorStore.smartmenuTopItemIndexToEdit !== undefined) {
                          this.props.navigatorStore.setSmartmenuTopItemEnableLikes();
                        } else if (this.props.navigatorStore.topItemIndexToEdit !== undefined) {
                          this.props.navigatorStore.setTopItemEnableLikes();
                        }
                        this.forceUpdate();
                      }}
                      style={{
                        marginTop: 20
                      }}
                    />
                    <IAToggle
                      label={this.localizationService.strings.Navigator_Content_EnableComments}
                      checked={selectedTopItem.config.enableComments !== undefined ? selectedTopItem.config.enableComments : true}
                      color={this.props.navigatorStore.adminColor}
                      onChange={() => {
                        if (this.props.navigatorStore.smartmenuTopItemIndexToEdit !== undefined) {
                          this.props.navigatorStore.setSmartmenuTopItemEnableComments();
                        } else if (this.props.navigatorStore.topItemIndexToEdit !== undefined) {
                          this.props.navigatorStore.setTopItemEnableComments();
                        }
                        this.forceUpdate();
                      }}
                      style={{
                        marginTop: 20
                      }}
                    />
                    <div className={adminStyles.panelDivider} />
                    <IAToggle
                      label={this.localizationService.strings.Navigator_Content_ShouldFilterByCategory}
                      checked={selectedTopItem.config.shouldFilterByCategory !== undefined ? selectedTopItem.config.shouldFilterByCategory : false}
                      color={this.props.navigatorStore.adminColor}
                      onChange={() => {
                        if (selectedTopItem != undefined) {
                          this.setState({ categoryValues: undefined });
                        }
                        selectedTopItem.config.shouldFilterByCategory = selectedTopItem.config.shouldFilterByCategory != undefined ? !selectedTopItem.config.shouldFilterByCategory : true;
                        if (selectedTopItem.config.shouldFilterByCategory) {
                          selectedTopItem.config.showFilter = false;
                        }
                        this.props.navigatorStore.checkForChanges();
                        this.forceUpdate();
                      }}
                      style={{
                        marginTop: 20
                      }}
                    />
                    {selectedTopItem.config.shouldFilterByCategory &&
                      <IATermCheckbox
                        environment={this.props.navigatorStore.environment}
                        token={this.props.navigatorStore.token}
                        tenant={this.props.navigatorStore.tenant.replace(".sharepoint.com", "")}
                        instance={selectedTopItem.config.instance}
                        component={"Event"}
                        selectedValues={selectedTopItem.config.selectedCategories}
                        highlightColor={this.props.navigatorStore.adminColor}
                        onChange={(selectedValues) => {
                          let categories = [];
                          selectedValues.forEach((item) => {
                            categories.push({ id: item.id, name: item.defaultLabel });
                          });
                          selectedTopItem.config.selectedCategories = categories;
                          this.props.navigatorStore.checkForChanges();
                          this.forceUpdate();
                        }}
                      />
                    }
                    <IAToggle
                      label={this.localizationService.strings.Navigator_Content_IsTargetingAvailable}
                      checked={selectedTopItem.config.isTargetingAvailable !== undefined ? selectedTopItem.config.isTargetingAvailable : true}
                      color={this.props.navigatorStore.adminColor}
                      onChange={() => {
                        selectedTopItem.config.isTargetingAvailable = selectedTopItem.config.isTargetingAvailable != undefined ? !selectedTopItem.config.isTargetingAvailable : false;
                        this.props.navigatorStore.checkForChanges();
                        this.forceUpdate();
                      }}
                      style={{
                        marginTop: 20
                      }}
                    />
                  </>
                }
                {selectedTopItem.type == "ai" &&
                  <TopItemSettingsAiConfiguration
                    selectedTopItem={selectedTopItem}
                  />
                }
                <div className={adminStyles.panelDivider} />
                {showTargetOptions &&
                  <div style={{
                    marginTop: "20px", clear: "both", float: "left", width: "100%", fontSize: "14px"
                  }}>
                    {this.props.navigatorStore.applicationSettings?.applicationSettings?.toLowerCase() === "profile data" ?
                      <TreeTargeting
                        application={"navigator"}
                        environment={this.props.navigatorStore.environment}
                        tenant={this.props.navigatorStore.tenant}
                        applicationData={this.props.navigatorStore.applicationSettings}
                        instance={this.props.navigatorStore.instance}
                        authToken={this.props.navigatorStore.token}
                        apiUrl={Environment.getEnvironmentForMainAPI(this.props.navigatorStore.environment)}
                        selectedValues={this.selectedValues}
                        operator={selectedTopItem.targeting ? selectedTopItem.targeting.operator : "AND"}
                        showTarget={selectedTopItem.targeting ? selectedTopItem.targeting.enabled : false}
                        currenNodeStructure={selectedTopItem.targetingExistingStructure}
                        onShowTargetChanged={(showTarget: boolean) => {
                          if (!selectedTopItem.targeting) {
                            selectedTopItem.targeting = {
                              enabled: showTarget,
                              operator: "AND"
                            };
                          } else {
                            selectedTopItem.targeting.enabled = showTarget;
                          }

                          if (this.props.navigatorStore.smartmenuTopItemIndexToEdit !== undefined) {
                            this.props.navigatorStore.updateSmartmenuTopIteTargeting(selectedTopItem.targeting);
                          } else if (this.props.navigatorStore.topItemIndexToEdit !== undefined) {
                            this.props.navigatorStore.updateTargeting(selectedTopItem.targeting);
                          }
                          this.props.navigatorStore.checkForChanges();
                        }}
                        onChange={(targeting, applicationSettings, groups, _selectAll, targetingExistingStructure) => {

                          // Processing targeting values
                          if (targeting) {
                            selectedTopItem.targeting.targetingFields = Utils.processReturnTreeTargeting(targeting);
                          }

                          // Processing targeting group data
                          if (groups) {
                            selectedTopItem.targeting.groupData = JSON.stringify(groups);
                            selectedTopItem.targeting.targetingGroups = [];

                            if (groups.length > 0) {
                              groups.forEach((x) => {
                                selectedTopItem.targeting.targetingGroups.push(x.id.toString());
                              });
                            }
                          }

                          if (this.props.navigatorStore.smartmenuTopItemIndexToEdit !== undefined) {
                            this.props.navigatorStore.updateSmartmenuTopIteTargeting(selectedTopItem.targeting);
                          } else if (this.props.navigatorStore.topItemIndexToEdit !== undefined) {
                            this.props.navigatorStore.updateTargeting(selectedTopItem.targeting);
                          }

                          this.setState({ applicationSettings: applicationSettings });
                          this.props.navigatorStore.applicationSettings = applicationSettings;
                          selectedTopItem.targetingExistingStructure = targetingExistingStructure;
                          this.props.navigatorStore.checkForChanges();
                        }}
                        highlightColor={this.props.navigatorStore.adminColor}
                        onLogicalOperatorChanged={(operator: string) => {
                          selectedTopItem.targeting.operator = operator;
                          if (this.props.navigatorStore.smartmenuTopItemIndexToEdit !== undefined) {
                            this.props.navigatorStore.updateSmartmenuTopIteTargeting(selectedTopItem.targeting);
                          } else if (this.props.navigatorStore.topItemIndexToEdit !== undefined) {
                            this.props.navigatorStore.updateTargeting(selectedTopItem.targeting);
                          }
                        }}
                        nodeComparison={"label"}
                      />
                      :
                      <TreeTargetingForGroups
                        environment={this.props.navigatorStore.environment}
                        tenant={this.props.navigatorStore.tenant.replace(".sharepoint.com", "")}
                        application={"navigator"}
                        instance={this.props.navigatorStore.instance}
                        applicationData={this.props.navigatorStore.applicationSettings}
                        selectedGroupIds={selectedTopItem?.targeting?.targetingGroups}
                        highlightColor={this.props.navigatorStore.adminColor}
                        showTarget={selectedTopItem.targeting != null ? selectedTopItem.targeting.enabled : false}
                        onShowTargetChanged={(showTarget: boolean) => {
                          if (!selectedTopItem.targeting) {
                            selectedTopItem.targeting = {
                              enabled: showTarget,
                              operator: "AND"
                            };
                          } else {
                            selectedTopItem.targeting.enabled = showTarget;
                          }
                          this.props.navigatorStore.checkForChanges();
                        }}
                        onGroupIdsUpdated={(ids: number[]) => {
                          selectedTopItem.targeting.targetingGroups = ids;
                          this.forceUpdate();
                          this.props.navigatorStore.checkForChanges();
                        }}
                      />
                    }
                    <div className={adminStyles.panelDivider} />
                  </div>
                }
                <div style={{
                  clear: "both", float: "left", width: "100%", fontSize: "14px"
                }}>
                  {!this.props.navigatorStore.isCreatingContent &&
                    <div
                      className={adminStyles.settingsPanelDeleteButton}
                      onClick={() => {
                        if (this.props.navigatorStore.smartmenuTopItemIndexToEdit !== undefined) {
                          this.props.navigatorStore.deleteSelectedSmartmenuTopItem();
                        } else if (this.props.navigatorStore.topItemIndexToEdit !== undefined) {
                          this.props.navigatorStore.deleteSelectedTopItem();
                        }
                      }}
                      style={{
                        backgroundColor: "#eeeeee"
                      }}
                    >
                      <div
                        className={adminStyles.settingsPanelDeleteButtonIcon}
                      >
                        <IAIcon
                          title="Trash"
                          size={20}
                          color="#333333"
                        />
                      </div>
                      <div
                        className={adminStyles.settingsPanelDeleteButtonText}
                        style={{
                          color: "#333333"
                        }}
                      >
                        {this.localizationService.strings.Navigator_TopItemPanel_Delete}
                      </div>
                    </div>
                  }
                  <div
                    className={adminStyles.settingsPanelDeleteButton}
                    onClick={() => {
                      if (localStorage.getItem(`navigator-${this.props.navigatorStore.instanceId}`) !== undefined && !this.props.navigatorStore.isCreatingContent) {
                        const originalNavigator = JSON.parse(localStorage.getItem(`navigator-${this.props.navigatorStore.instanceId}`));
                        if (this.props.navigatorStore.smartmenuTopItemIndexToEdit !== undefined) {
                          this.props.navigatorStore.navigator.smartmenu.elements[this.props.navigatorStore.smartmenuTopItemIndexToEdit] = originalNavigator.smartmenu.elements[this.props.navigatorStore.smartmenuTopItemIndexToEdit];
                        } else if (this.props.navigatorStore.topItemIndexToEdit !== undefined) {
                          this.props.navigatorStore.navigator.megamenu.topItems[this.props.navigatorStore.topItemIndexToEdit] = originalNavigator.megamenu.topItems[this.props.navigatorStore.topItemIndexToEdit];
                        }
                        this.props.navigatorStore.checkForNewContent();
                        this.props.navigatorStore.topItemIndexToEdit = undefined;
                        this.props.navigatorStore.smartmenuTopItemIndexToEdit = undefined;
                        this.props.navigatorStore.toggleTopItemSettings();
                      }
                      if (this.props.navigatorStore.isCreatingContent) {
                        if (this.props.navigatorStore.smartmenuTopItemIndexToEdit !== undefined) {
                          this.props.navigatorStore.deleteSelectedSmartmenuTopItem();
                        } else if (this.props.navigatorStore.topItemIndexToEdit !== undefined) {
                          this.props.navigatorStore.deleteSelectedTopItem();
                        }
                      }
                    }}
                    style={{
                      backgroundColor: "#eeeeee"
                    }}
                  >
                    <div className={adminStyles.settingsPanelDeleteButtonText}>
                      {this.localizationService.strings.Navigator_TopItemPanel_Cancel}
                    </div>
                  </div>
                </div>
              </>
            }
            {this.props.navigatorStore.isShowingTopItemSettings && (this.props.navigatorStore.droppableSmartmentTopItemSpotsAreShowing || this.props.navigatorStore.droppableTopItemSpotsAreShowing) &&
              <>
                {pages}
              </>
            }
          </div>
        </div >
      );
    } else {
      return (<></>);
    }
  }
}